import { Settings } from '@/constants'
import { TFunction } from 'i18next'

interface Message {
    role: string
    content: string
    timestamp?: string
}

interface Chat {
    id?: string
    name?: string
}

export const getQuestion = (messages: Message[]): string => {
    const firstUserMessage = getFirstUserMessage(messages)
    return firstUserMessage ? firstUserMessage.content : ''
}

export const getDate = (messages: Message[]): string | null => {
    const assistantMessages = getAssistantMessages(messages)
    if (assistantMessages?.length < 2) return null

    const secondAssistantMessage = assistantMessages?.[1]
    return extractDate(secondAssistantMessage)
}

export const isEmptyChat = (chat: Chat): boolean =>
    chat?.id === undefined || chat?.name === Settings.emptyChat

export const holdsEmptyChat = (chats: Chat[]): boolean =>
    chats?.some((chat) => isEmptyChat(chat))

const getAssistantMessages = (messages: Message[]): Message[] =>
    messages?.filter(({ role }) => role === 'assistant')

const getUserMessages = (messages: Message[]): Message[] =>
    messages?.filter(({ role }) => role === 'user')

const getFirstUserMessage = (messages: Message[]): Message | null => {
    if (messages?.length < 2) return null

    const [firstUserMessage] = getUserMessages(messages)

    return firstUserMessage || null
}

const extractDate = (message: Message): string | null => {
    if (!message) return null
    const { timestamp } = message
    return timestamp ? new Date(timestamp).toLocaleDateString() : null
}

export const getTimestampRefreshDelay = (timestamp: string | number | undefined): number => {
    if (!timestamp) return 0

    const timestampNum = typeof timestamp === 'string' ? Number(timestamp) : timestamp
    if (Number.isNaN(timestampNum)) return 0

    const diffInMs = Date.now() - timestampNum
    if (diffInMs < 0) return 60000

    const diffInMinutes = Math.floor(diffInMs / 60000)
    if (diffInMinutes >= 60) return 0

    return 60000 - (diffInMs % 60000)
}

// formatTimestamp.ts
export const formatTimestamp = (timestamp: string | number | undefined, t: TFunction): string => {
    if (!timestamp) return ''

    // Convert string to number if needed
    const timestampNum = typeof timestamp === 'string' ? Number(timestamp) : timestamp

    // Validate the timestamp
    if (Number.isNaN(timestampNum)) return ''

    const messageDate = new Date(timestampNum)

    // Check if the date is valid
    if (Number.isNaN(messageDate.getTime())) return ''

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const timeDiffInMs = today.getTime() - messageDate.getTime()
    const timeDiffInMinutes = Math.floor(timeDiffInMs / 60000) // Convert ms to minutes

    const isToday = messageDate.toDateString() === today.toDateString()
    const isYesterday = messageDate.toDateString() === yesterday.toDateString()
    const isJustNow = timeDiffInMinutes < 1 // Less than 1 minute ago
    const isMinAgo = timeDiffInMinutes >= 1 && timeDiffInMinutes < 60 // Between 1 minute and 60 minutes

    // Handle "just now"
    if (isJustNow) {
        return t('general:time:justNow') // Pass translation key
    }

    // Handle "minutes ago"
    if (isMinAgo) {
        return `${t('general:time:minAgo', { count: timeDiffInMinutes })}` // Pass translation key with count
    }

    // Format time for "today" or "yesterday"
    const timeString = messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })

    if (isToday) {
        return `${t('general:time:today')}, ${timeString}`
    } else if (isYesterday) {
        return `${t('general:time:yesterday')}, ${timeString}`
    } else {
        const dateString = messageDate.toLocaleDateString([], {
            weekday: 'short',
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
        })
        return dateString
    }
}
